import { Avatar, Divider, Typography } from "@material-ui/core"
import { Person } from "@material-ui/icons"
import React from "react"
import DefaultButton from "../../../DefaultButton"
import parseVCard from "../utils/parseVCard"

/**
 * @typedef {Object} Contact
 * @property {string} name - Nome do contato extraído do vCard.
 * @property {string} number - Número de telefone do contato.
 * @property {string} [photo] - Imagem em base64, se disponível.
 */

/**
 * Renderiza o conteúdo de uma mensagem do tipo "contato", tratando múltiplos ou único contato.
 * @param {import("../MessageItem").Message} message
 * @param {import("@material-ui/styles").ClassNameMap} classes
 * @param {Function} setInitialValues - Função que define os valores iniciais no modal de contato.
 * @param {Function} setContactModalOpen - Função que abre o modal para salvar contato.
 * @param {Function} openViewContactsModal - Função que abre o modal de múltiplos contatos.
 * @returns {JSX.Element} Elemento JSX representando o(s) contato(s) renderizado(s).
 */
const renderContactCard = (message, classes, setInitialValues, setContactModalOpen, openViewContactsModal) => {
  const vcards = message.body.split("||")

  /** @type {Contact[]} */
  const contacts = vcards.map(parseVCard)

  // Renderização condicional
  if (contacts.length > 1) return renderMultipleContacts(contacts, classes, openViewContactsModal)
  return renderSingleContact(contacts[0], classes, setInitialValues, setContactModalOpen)
}

/**
 * Renderiza o avatar do contato, exibindo a imagem base64 (se disponível) ou o ícone/padrão.
 * @param {Contact} contact - Dados do contato.
 * @param {import("@material-ui/styles").ClassNameMap} classes
 * @param {number} [index=0] - Índice do contato para gerar keys únicas.
 * @returns {JSX.Element} Avatar renderizado.
 */
const renderContactAvatar = (contact, classes, index = 0) => {
  const key = `contact-avatar-${index}`

  if (contact.photo) {
    return (
      <img
        key={key}
        src={`data:image/jpeg;base64,${contact.photo}`}
        alt="Contact"
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
    )
  }

  const isNumberName = contact.name?.charAt(0) === "+" || !isNaN(contact.name?.charAt(0))

  return (
    <Avatar key={key} className={classes?.avatar}>
      {isNumberName ? <Person /> : contact.name?.charAt(0)}
    </Avatar>
  )
}

/**
 * Renderiza o layout para múltiplos contatos.
 * @param {Contact[]} contacts - Lista de contatos a serem exibidos.
 * @param {import("@material-ui/styles").ClassNameMap} classes
 * @param {Function} openViewContactsModal - Função para abrir o modal de múltiplos contatos.
 * @returns {JSX.Element} Elemento JSX.
 */
const renderMultipleContacts = (contacts, classes, openViewContactsModal) => {
  return (
    <div style={{ minWidth: "200px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 5 }}>
        {contacts.slice(0, 3).map((contact, index) => renderContactAvatar(contact, classes, index))}
        {contacts.length > 3 && <Avatar className={classes?.avatar}>+{contacts.length - 3}</Avatar>}
      </div>

      <Divider style={{ marginTop: 5, marginBottom: 5 }} />

      <DefaultButton
        stylization="contact"
        onClick={() => openViewContactsModal(contacts)}
        style={{ display: "flex", width: "100%" }}
      >
        Ver Contatos
      </DefaultButton>
    </div>
  )
}

/**
 * Renderiza o layout para um único contato.
 * @param {Contact} contact - Contato único a ser exibido.
 * @param {import("@material-ui/styles").ClassNameMap} classes
 * @param {Function} setInitialValues - Função que define valores iniciais do modal de contato.
 * @param {Function} setContactModalOpen - Função para abrir o modal de contato.
 * @returns {JSX.Element} Elemento JSX.
 */
const renderSingleContact = (contact, classes, setInitialValues, setContactModalOpen) => {
  const initialValues = {
    name: contact.name,
    number: contact.number?.replace(/\D/g, ""),
  }

  const handleSaveContact = () => {
    setInitialValues(initialValues)
    setContactModalOpen(true)
  }

  return (
    <div style={{ minWidth: "200px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {renderContactAvatar(contact, classes)}
        <Typography variant="subtitle1" noWrap={contact.name.length > 50}>
          {contact.name}
        </Typography>
      </div>

      <Typography style={{ display: "flex", alignItems: "center", justifySelf: "center" }} variant="body2">
        {contact.number}
      </Typography>

      <Divider style={{ marginTop: 5, marginBottom: 5 }} />

      <DefaultButton stylization="contact" onClick={handleSaveContact} style={{ display: "flex", width: "100%" }}>
        Salvar contato
      </DefaultButton>
    </div>
  )
}

export default renderContactCard
